import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ClipboardList,
  SquareCheckBig,
  Sparkles,
  Library,
  Book,
  ChevronsUpDown,
  Check,
  GraduationCap,
  Folder,
  Layers,
  PanelLeftClose,
  Pin,
  Plus,
  Settings,
  LogOut,
  History,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import { Skeleton } from 'components/ui/skeleton';
import { cn, resolveOrgLogoSrc } from 'lib/utils';
import { useIsDesktop } from 'hooks/use-media-query';
import * as AuthModule from 'modules/auth';
import { usePendingGrading } from 'modules/grading';
import { useFeature, useUpgradeState, FEATURES } from 'modules/entitlements';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS, type Permission } from 'modules/auth/permissions';
import { useSidebarStore } from 'store/sidebar.store';
import { useUpgradeDialogStore } from 'store/upgradeDialog';
import LogoMark from './LogoMark';
import { SidebarSearchButton } from './CommandPalette';
import { useCreateActions } from './HeaderCreateMenu';
import { HeaderCoins } from './HeaderCoins';
import { IS_IO } from 'lib/site';
import * as AuthApi from 'modules/auth/api';
import { isAccessContextReady, loadAccessContext } from 'modules/auth/access';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import config from 'config';

/** Matches the 768px threshold: at md+ the sidebar is persistent, so a nav click shouldn't collapse it. */
function closeSidebarOnMobile() {
  if (window.innerWidth < 768) {
    useSidebarStore.getState().setIsOpen(false);
  }
}

// =============================================================================
// Nav item + section label
// =============================================================================

interface NavItemProps {
  icon: React.ElementType;
  label: string;
  to: string;
  active: boolean;
  /** Right-aligned attention count (e.g. pending grading). Hidden when 0. */
  badge?: number;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, to, active, badge }) => (
  <Link
    to={to}
    onClick={closeSidebarOnMobile}
    aria-current={active ? 'page' : undefined}
    className={cn(
      'relative flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors',
      active
        ? 'bg-foreground font-semibold text-background'
        : 'font-medium text-foreground/70 hover:bg-accent hover:text-foreground',
    )}
  >
    <Icon
      className={cn('h-4 w-4 shrink-0', active ? 'text-background' : 'text-muted-foreground')}
      strokeWidth={1.75}
    />
    <span className="truncate">{label}</span>
    {badge != null && badge > 0 && (
      <span className="ml-auto shrink-0 rounded-full bg-error-50 px-1.5 text-[10px] font-bold leading-[18px] text-error-700 dark:bg-error-900/40 dark:text-error-300">
        {badge}
      </span>
    )}
  </Link>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-2 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
    {children}
  </p>
);

// Small inline org-logo block. Mirrors the Avatar component's render-or-initial
// fallback, but for org logos served via the /api/organizations/:id/logo proxy.
const OrgLogo: React.FC<{
  org: { id: string; name: string; logoUrl?: string };
  size: number;
}> = ({ org, size }) => {
  const [errored, setErrored] = useState(false);
  const src = !errored ? resolveOrgLogoSrc(config.api.base_url, org.id, org.logoUrl) : undefined;
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-lg font-bold',
        // Real logo when present; else an ink initial (lime is kept for actions
        // and the signed-in user).
        src ? 'bg-muted text-foreground' : 'bg-foreground text-background',
      )}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.floor(size * 0.4)) }}
    >
      {src ? (
        <img
          src={src}
          alt={org.name}
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        org.name?.charAt(0)?.toUpperCase() || 'O'
      )}
    </div>
  );
};

// =============================================================================
// Org Switcher — top of sidebar (Popover): switch org + org settings
// =============================================================================

const OrgSwitcher: React.FC = () => {
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  // Desktop nests the org list in a side-opening submenu (Linear-style). Inside
  // the ~228px mobile drawer that submenu has no room to open and gets clipped,
  // so on mobile we render the list inline in the main menu instead.
  const isDesktop = useIsDesktop();

  const {
    state: { organization, organizations, permissions, entitlements },
    methods,
  } = AuthModule.Context.useAuthContext();

  // Subtitle under the org name: the active plan (e.g. "Pro plan"), falling
  // back to the org slug before entitlements resolve.
  const planLabel = entitlements?.planName
    ? /plan/i.test(entitlements.planName)
      ? entitlements.planName
      : `${entitlements.planName} ${t('plan')}`
    : organization?.slug;

  const handleSwitchOrg = async (orgId: string) => {
    if (orgId === organization?.id || switching) return;
    setSwitching(true);
    try {
      const { data } = await AuthApi.SwitchOrganization({ organizationId: orgId });
      methods.setOrganization({ id: data.organization.id, name: data.organization.name, slug: data.organization.slug });
      methods.setRole(data.role);
      // Persist the new active org FIRST so the X-Organization-Id header points at
      // it, then load permissions + entitlements for it from their dedicated
      // endpoints (no longer carried on the switch response). Awaited so the new
      // shell renders with the correct access instead of flashing the old org's.
      localStorage.setItem(config.api.active_org_key, data.organization.id);
      await loadAccessContext(methods);
      closeSidebarOnMobile();
      navigate('/app');
    } catch {
      // Never fail silently: if the switch call failed the user stays on the
      // current org; if it succeeded but loadAccessContext threw, org identity
      // may be ahead of permissions/entitlements, so prompt a retry rather than
      // leaving the shell in a half-switched state with no feedback.
      toast.error(t('org_switch_failed', 'Could not switch organization'), {
        description: t('org_switch_failed_desc', 'Please try again.'),
      });
    } finally {
      setSwitching(false);
    }
  };

  if (!organization) return null;

  // The list of switchable orgs — reused by the desktop submenu and the inline
  // mobile section so both stay in sync.
  const orgItems = organizations.map((org) => (
    <DropdownMenuItem
      key={org.id}
      disabled={switching}
      onClick={() => handleSwitchOrg(org.id)}
      className="gap-2.5"
    >
      <OrgLogo size={24} org={org} />
      <span className="min-w-0 flex-1 truncate font-medium text-foreground">{org.name}</span>
      {org.id === organization.id && <Check className="h-4 w-4 shrink-0 text-foreground" />}
    </DropdownMenuItem>
  ));

  return (
    <div className="mb-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex w-full items-center gap-2.5 rounded-lg border border-border p-2 text-left transition-colors hover:bg-accent">
            <OrgLogo size={26} org={organization} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-surface-base font-semibold text-foreground">{organization.name}</p>
              <p className="truncate text-[11px] text-muted-foreground">{planLabel}</p>
            </div>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        {/* Workspace menu (design port): the switchable org list sits inline at
            the top, then Settings + Sign out with leading icons. */}
        <DropdownMenuContent
          data-sidebar-menu
          align="start"
          sideOffset={6}
          className={cn('min-w-[220px] p-1.5', isDesktop ? 'w-[var(--radix-dropdown-menu-trigger-width)]' : 'w-64')}
        >
          <div className="max-h-64 overflow-y-auto">{orgItems}</div>
          <DropdownMenuSeparator />
          {/* Settings opens the dialog: org profile for admins, own profile
              otherwise (everyone has at least the Profile section). */}
          <DropdownMenuItem
            className="gap-2.5"
            onClick={() => {
              closeSidebarOnMobile();
              openSettings(permissions.includes(PERMISSIONS.ROLES_EDIT) ? 'org' : 'profile');
            }}
          >
            <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />
            {t('settings.title')}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2.5"
            onClick={() => {
              closeSidebarOnMobile();
              methods.logout();
            }}
          >
            <LogOut className="h-4 w-4 shrink-0 text-muted-foreground" />
            {t('usermenu_signout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// =============================================================================
// Create button — sidebar quick-create (redesign shell). The header went
// minimal (profile + notifications only), so the "Create" menu moved here,
// under the search pill. Same permission/feature-filtered actions as the mobile
// create FAB via the shared useCreateActions hook; renders nothing when the
// member can't create anything.
// =============================================================================

const SidebarCreateButton: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = useCreateActions();

  if (actions.length === 0) return null;

  return (
    <div className="mb-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
            <Plus className="h-4 w-4" aria-hidden />
            {t('create', 'Create')}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent data-sidebar-menu align="start" sideOffset={6} className="w-[220px]">
          {actions.map((a) => (
            <DropdownMenuItem
              key={a.to}
              onClick={() => {
                closeSidebarOnMobile();
                navigate(a.to);
              }}
              className="gap-2.5"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-foreground/70">
                <a.icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              </span>
              <span className="text-sm">{a.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// =============================================================================
// Upgrade card — sidebar footer (design shell). Only rendered when the org
// should actually be nudged (trial ending/over, free, expired, payment issue)
// per useUpgradeState — real state, never a fabricated countdown. CTA mirrors
// the header plan badge: billing-capable → billing settings; else the upgrade
// dialog's "ask your admin" path.
// =============================================================================

const SidebarUpgradeCard: React.FC = () => {
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  const canEdit = usePermission(PERMISSIONS.BILLING_EDIT);
  const { showUpgrade, reason, daysLeft } = useUpgradeState();

  if (!showUpgrade) return null;

  const isTrialCountdown =
    (reason === 'trial_ending' || reason === 'trial_over') && daysLeft != null && daysLeft > 0;
  const title = isTrialCountdown
    ? t('trial_days_left', { count: daysLeft as number, defaultValue: `${daysLeft} days left` })
    : t('upgrade_plan', 'Upgrade plan');

  const onUpgrade = () => {
    closeSidebarOnMobile();
    if (canEdit) {
      openSettings('billing');
      return;
    }
    useUpgradeDialogStore.getState().open({ code: 'FEATURE_NOT_ENTITLED' });
  };

  return (
    // A plain card: the state in words, and the lime button as the one action.
    <div className="mb-2 rounded-lg border border-border bg-card p-3">
      <p className="text-[13px] font-semibold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
        {t('sidebar_upgrade_blurb', 'Unlock every premium feature.')}
      </p>
      <button
        type="button"
        onClick={onUpgrade}
        className="mt-2.5 h-8 w-full rounded-md bg-primary text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {t('upgrade', 'Upgrade')}
      </button>
    </div>
  );
};

// =============================================================================
// Studio nav data — grouped items (INSIGHTS / ASSESS / CONTENT / PEOPLE / HISTORY),
// permission-filtered. Shared by the expanded sidebar and the collapsed navy
// icon rail so both render one source of truth ("one component, two widths").
// =============================================================================

export interface StudioNavItem {
  icon: React.ElementType;
  label: string;
  to: string;
  active: boolean;
  badge?: number;
}
export interface StudioNavGroup {
  /** Empty for a group that needs no heading (Analytics, whose only item says it already). */
  label: string;
  items: StudioNavItem[];
}

interface NavEntry extends StudioNavItem {
  show: boolean;
}

export function useStudioNavGroups(): { groups: StudioNavGroup[]; isFetched: boolean } {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();

  const {
    state: authState,
  } = AuthModule.Context.useAuthContext();
  const { permissions: p, organization } = authState;

  const hasCollections = useFeature(FEATURES.COLLECTIONS);
  const hasTeams = useFeature(FEATURES.TEAMS);
  const hasIeltsSuite = useFeature(FEATURES.IELTS_SUITE);
  const hasSatSuite = useFeature(FEATURES.SAT_SUITE);
  const hasExamSuite = hasIeltsSuite || hasSatSuite;

  // Attention badge on the Grading nav item — the org's pending manual-grading
  // queue length. Only fetched for graders (candidates never see the item).
  const canGrade = p.includes(PERMISSIONS.GRADING_GRADE);
  const gradingCount = usePendingGrading(canGrade ? organization?.id : undefined).data?.length ?? 0;

  const base = '/app';
  const has = (perm: Permission) => p.includes(perm);
  const isAt = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

  const groups: { label: string; items: NavEntry[] }[] = [
    {
      label: '',
      items: [
        {
          icon: BarChart3,
          label: t('nav.analytics'),
          to: `${base}/analytics`,
          show: has(PERMISSIONS.TESTS_VIEW_RESULTS),
          active: pathname === base || isAt(`${base}/analytics`) || isAt(`${base}/dashboard`),
        },
      ],
    },
    {
      label: t('nav.group.assess'),
      items: [
        {
          icon: ClipboardList,
          label: t('nav.tests'),
          to: `${base}/tests`,
          show: has(PERMISSIONS.TESTS_LIST),
          active: pathname.startsWith(`${base}/tests`) && !pathname.includes('create'),
        },
        {
          icon: SquareCheckBig,
          label: t('nav.grading'),
          to: `${base}/grading`,
          show: has(PERMISSIONS.GRADING_GRADE),
          active: isAt(`${base}/grading`),
          badge: gradingCount,
        },
        {
          icon: History,
          label: t('nav.history', 'History'),
          to: `${base}/history`,
          show: true,
          active: isAt(`${base}/history`),
        },
      ],
    },
    {
      label: t('nav.group.content'),
      items: [
        {
          icon: Book,
          label: t('nav.subjects'),
          to: `${base}/subjects`,
          show: has(PERMISSIONS.SUBJECTS_VIEW),
          active: isAt(`${base}/subjects`),
        },
        {
          icon: Library,
          label: t('nav.question_bank'),
          to: `${base}/questions`,
          show: has(PERMISSIONS.QUESTIONS_VIEW),
          active: isAt(`${base}/questions`),
        },
        {
          icon: Sparkles,
          label: t('nav.ai_generate'),
          to: `${base}/documents`,
          show: has(PERMISSIONS.DOCUMENTS_EDIT),
          active: isAt(`${base}/documents`) || isAt(`${base}/learn`),
        },
        {
          // P10: reusable IELTS sections and SAT module sets for random versions.
          icon: Layers,
          label: t('exam.library.title'),
          to: `${base}/section-library`,
          show: hasExamSuite && has(PERMISSIONS.TESTS_EDIT),
          active: isAt(`${base}/section-library`),
        },
        {
          icon: Folder,
          label: t('nav.library'),
          to: `${base}/library`,
          show: hasCollections && has(PERMISSIONS.COLLECTIONS_LIST),
          active: isAt(`${base}/library`) || isAt(`${base}/collections`),
        },
      ],
    },
    {
      label: t('nav.group.people'),
      items: [
        {
          icon: GraduationCap,
          label: t('nav.groups'),
          to: `${base}/teams`,
          show: hasTeams && has(PERMISSIONS.TEAMS_LIST),
          active: pathname.startsWith(`${base}/teams`),
        },
        // Members is intentionally NOT here. It lives in the settings dialog
        // (`?settings=members`), reachable from the org switcher, the Members
        // page itself, and ⌘K — the nav entry was a duplicate route into it.
      ],
    },
  ]
    .map((group) => ({ ...group, items: group.items.filter((item) => item.show) }))
    .filter((group) => group.items.length > 0);

  return { groups, isFetched: isAccessContextReady(authState) };
}

// =============================================================================
// Left Sidebar Content — the expanded 228px grouped nav.
// =============================================================================

export const LeftSidebarContent: React.FC = React.memo(function LeftSidebarContent() {
  const { t } = useTranslation();
  const { isOpen, toggleOpen } = useSidebarStore();
  const { groups, isFetched } = useStudioNavGroups();

  // Show loading skeleton while fetching user data
  if (!isFetched) {
    return (
      <nav className="h-full w-full px-2">
        <div className="space-y-6">
          <Skeleton className="h-14 w-full rounded-lg" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20 rounded" />
              {[0, 1, 2].map((j) => (
                <Skeleton key={j} className="h-9 w-full rounded" />
              ))}
            </div>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav className="flex h-full w-full flex-col px-2">
      {/* Sidebar header — brand + collapse-to-rail control (desktop). */}
      <div className="mb-2 flex items-center justify-between gap-2 pl-1 pr-0.5 pt-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <LogoMark size={28} />
          <span className="truncate font-display text-sm font-extrabold tracking-[-0.02em] text-foreground">{t('examing')}</span>
        </div>
        <button
          onClick={toggleOpen}
          aria-label={t(isOpen ? 'collapse_sidebar' : 'pin_sidebar')}
          title={t(isOpen ? 'collapse_sidebar' : 'pin_sidebar')}
          className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:flex"
        >
          {isOpen ? <PanelLeftClose className="h-[18px] w-[18px]" /> : <Pin className="h-[18px] w-[18px]" />}
        </button>
      </div>
      <OrgSwitcher />
      <SidebarSearchButton />
      <SidebarCreateButton />

      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto scrollbar-thin pb-2">
        {groups.map((group) => (
          <div key={group.label}>
            {group.label && <SectionLabel>{group.label}</SectionLabel>}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem
                  key={item.to}
                  icon={item.icon}
                  label={item.label}
                  to={item.to}
                  active={item.active}
                  badge={item.badge}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer — coin balance (billing-capable only) + upgrade nudge (real
          state only). The account entry moved to the header profile menu; both
          items below self-hide, so this collapses to nothing when neither
          applies. */}
      <div className="mt-1 space-y-1">
        {!IS_IO && <HeaderCoins />}
        <SidebarUpgradeCard />
      </div>
    </nav>
  );
});

export default LeftSidebarContent;
